/**
 * Content Loader Utility
 * 
 * Provides helper functions for loading internationalized markdown content files.
 * Automatically handles language suffixes and fallback to English.
 */

/**
 * Get the content file path with appropriate language suffix
 * 
 * @param {string} basePath - Base path without extension (e.g., "/content/_index")
 * @param {string} lang - Language code (e.g., "en", "ja", "es")
 * @returns {string} Full path with language suffix (e.g., "/content/_index.ja.md")
 * 
 * @example
 * getContentPath("/content/_index", "ja") // Returns: "/content/_index.ja.md"
 * getContentPath("/content/_index", "en") // Returns: "/content/_index.md"
 */
const normalizeLanguage = (lang = 'en') => {
  if (!lang) {
    return 'en';
  }
  return lang.split('-')[0].toLowerCase();
};

export const getContentPath = (basePath, lang) => {
  const normalizedLang = normalizeLanguage(lang);
  const suffix = normalizedLang === 'en' ? '' : `.${normalizedLang}`;
  return `${basePath}${suffix}.md`;
};

/**
 * Get the content file path with fallback support
 * 
 * Tries to load the language-specific version first, falls back to English if not found.
 * 
 * @param {string} basePath - Base path without extension
 * @param {string} lang - Language code
 * @returns {Promise<string>} Path to the content file
 * 
 * @example
 * // If Japanese file doesn't exist, returns English path
 * await getContentPathWithFallback("/content/contact/_index", "ja")
 */
export const getContentPathWithFallback = async (basePath, lang) => {
  const normalizedLang = normalizeLanguage(lang);
  const primaryPath = getContentPath(basePath, normalizedLang);
  
  // If requesting English, return immediately
  if (normalizedLang === 'en') {
    return primaryPath;
  }
  
  // Try to fetch the language-specific file
  try {
    const response = await fetch(primaryPath, { method: 'HEAD' });
    if (response.ok) {
      return primaryPath;
    }
  } catch (error) {
    console.warn(`Content file not found: ${primaryPath}, falling back to English`);
  }
  
  // Fallback to English
  return getContentPath(basePath, 'en');
};

/**
 * Load markdown content with gray-matter parsing
 * 
 * @param {string} filePath - Full path to the markdown file
 * @returns {Promise<{frontMatter: object, content: string}>} Parsed frontmatter and content
 * 
 * @example
 * const { frontMatter, content } = await loadMarkdownContent("/content/_index.md");
 * console.log(frontMatter.title); // Access frontmatter
 * console.log(content); // Access markdown content
 */
export const loadMarkdownContent = async (filePath) => {
  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filePath}: ${response.status}`);
    }
    
    const text = await response.text();
    
    // Dynamic import to avoid bundling gray-matter if not needed
    const matter = (await import('gray-matter')).default;
    const { data, content } = matter(text.trim());
    
    return {
      frontMatter: data || {},
      content: content || ''
    };
  } catch (error) {
    console.error(`Error loading content from ${filePath}:`, error);
    return {
      frontMatter: {},
      content: ''
    };
  }
};

/**
 * Load content with automatic language detection and fallback
 * 
 * Complete solution that handles path resolution, fallback, and content parsing.
 * 
 * @param {string} basePath - Base path without extension
 * @param {string} lang - Language code
 * @returns {Promise<{frontMatter: object, content: string}>} Parsed content
 * 
 * @example
 * const { frontMatter, content } = await loadContent("/content/_index", "ja");
 */
export const loadContent = async (basePath, lang) => {
  const filePath = await getContentPathWithFallback(basePath, lang);
  return loadMarkdownContent(filePath);
};

export default {
  getContentPath,
  getContentPathWithFallback,
  loadMarkdownContent,
  loadContent
};
